// Created By System on 29-Jan-2026
// Mounting Structure BOM Form Component with Line Items
// Fixed By System on 06-Feb-2026 - Prevent duplication during item loading
import React, { useState, useEffect, useRef } from "react";
import { Col, Row, Table, Button as BootstrapButton, Card } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import Select, { components } from "react-select";

const MountingStructureForm = ({ bom, items, onBomChange, onItemsChange }) => {
  // Group legacy flat items into line items structure when loading existing data
  // Updated by Farhan Khan on 31-Jan-2026 - Parse parts from JSONB and normalize
  // Fixed By System on 06-Feb-2026 - Prevent duplication by tracking if items have been processed
  // Fixed By System on 06-Feb-2026 - Use ref to track processed items to prevent re-processing
  const [itemsProcessed, setItemsProcessed] = useState(false);
  const [initialItemsLength, setInitialItemsLength] = useState(null);
  const processedItemsRef = React.useRef(null); // Track the items we've already processed
  
  useEffect(() => {
    // Fixed By System on 06-Feb-2026 - Only process items once on initial load to prevent duplication
    // Check if we've already processed these exact items
    const itemsKey = items?.length ? `${items.length}_${items[0]?.id || items[0]?.roof_type || 'empty'}` : 'empty';
    
    if (itemsProcessed && processedItemsRef.current === itemsKey) {
      return; // Already processed these items, don't run again
    }
    
    // If items is empty, mark as processed and don't do anything
    if (!items || items.length === 0) {
      if (!itemsProcessed) {
        setItemsProcessed(true);
        processedItemsRef.current = 'empty';
        setInitialItemsLength(0);
      }
      return;
    }
    
    if (items && items.length > 0) {
      // Parse parts from JSONB if stored as string
      const itemsWithParsedParts = items.map(item => {
        if (item.parts) {
          // If parts is a string (JSONB), parse it
          if (typeof item.parts === 'string') {
            try {
              item.parts = JSON.parse(item.parts);
            } catch (e) {
              console.warn('Failed to parse parts JSONB:', e);
              item.parts = [];
            }
          }
          // Ensure parts is an array and normalize
          if (Array.isArray(item.parts)) {
            item.parts = normalizeParts(item.parts);
          } else {
            item.parts = [];
          }
        } else {
          item.parts = [];
        }
        return item;
      });
      
      // Check if items already have the new structure (with parts array)
      const hasNewStructure = itemsWithParsedParts.some(item => item.parts && Array.isArray(item.parts) && item.parts.length > 0);
      
      // Also check if items are already in line item format (have roof_type at top level)
      const isLineItemFormat = itemsWithParsedParts.some(item => item.roof_type && !item.component_name);
      
      if (!hasNewStructure && !isLineItemFormat) {
        // Group items by roof_type and structure_height to form line items
        const groupedItems = {};
        const specialStructures = [];
        const comboDistributions = [];
        
        itemsWithParsedParts.forEach(item => {
          // Handle special structures separately
          if (item.item_type === 'SPECIAL_STRUCTURE') {
            specialStructures.push(item);
            return;
          }
          
          // Handle combo distributions separately
          if (item.item_type === 'COMBO_DISTRIBUTION') {
            comboDistributions.push(item);
            return;
          }
          
          // Group component items by roof_type and structure_height
          // Updated by Farhan Khan on 31-Jan-2026 - Normalize roof_type for grouping
          const normalizedRoofType = normalizeRoofType(item.roof_type || item.combo_roof_type);
          const key = `${normalizedRoofType || 'UNKNOWN'}_${item.structure_height_front || '0'}_${item.structure_height_back || '0'}`;
          
          if (!groupedItems[key]) {
            groupedItems[key] = {
              id: item.id || `temp_${Date.now()}_${key}`,
              s_no: item.s_no,
              item_type: "COMPONENT",
              roof_type: normalizedRoofType || "",
              structure_height_front: item.structure_height_front || null,
              structure_height_back: item.structure_height_back || null,
              parts: [],
              tin_shed_structure_type: item.tin_shed_structure_type || null,
              tin_shed_mounting_type: item.tin_shed_mounting_type || null,
              combo_panels_rcc: item.combo_panels_rcc || null,
              combo_panels_ground: item.combo_panels_ground || null,
              combo_panels_tinshed: item.combo_panels_tinshed || null,
              special_structure_type: null,
              special_structure_specs: "",
              special_structure_length: null,
              cleaning_system: "",
              others_text: "",
              status: item.status || "Active"
            };
          }
          
          // Add part from component item
          // Updated by Farhan Khan on 31-Jan-2026 - Use component_name consistently
          if (item.component_name) {
            const specsArray = parseJsonArray(item.specs);
            const shapeArray = parseJsonArray(item.shape);
            const makeArray = parseJsonArray(item.make);
            
            // Fixed by Farhan Khan on 31-Jan-2026 - Store shape and make as arrays for multi-select
            groupedItems[key].parts.push({
              component_name: item.component_name,
              specs: specsArray.length > 0 ? specsArray[0] : "",
              size: item.size || "",
              shape: shapeArray, // Keep as array for multi-select
              make: makeArray,   // Keep as array for multi-select
              others: item.others || ""
            });
          }
        });
        
        // Convert grouped items to array
        const newItems = Object.values(groupedItems);
        
        // Add special structures as separate line items
        // Updated by Farhan Khan on 31-Jan-2026 - Include parsed parts and normalize roof_type
        specialStructures.forEach(item => {
          const normalizedRoofType = normalizeRoofType(item.roof_type || item.combo_roof_type);
          const parsedParts = Array.isArray(item.parts) ? normalizeParts(item.parts) : [];
          newItems.push({
            id: item.id || `temp_${Date.now()}_special`,
            s_no: item.s_no,
            item_type: "SPECIAL_STRUCTURE",
            roof_type: normalizedRoofType || "",
            structure_height_front: item.structure_height_front || null,
            structure_height_back: item.structure_height_back || null,
            parts: parsedParts,
            tin_shed_structure_type: null,
            tin_shed_mounting_type: null,
            combo_panels_rcc: null,
            combo_panels_ground: null,
            combo_panels_tinshed: null,
            special_structure_type: item.special_structure_type,
            special_structure_specs: item.specs || "",
            special_structure_length: item.length || null,
            cleaning_system: item.notes || "",
            others_text: "",
            status: item.status || "Active"
          });
        });
        
        // Add combo distributions as separate line items
        // Updated by Farhan Khan on 31-Jan-2026 - Include parsed parts and normalize roof_type
        comboDistributions.forEach(item => {
          const normalizedRoofType = normalizeRoofType(item.combo_roof_type || "COMBO");
          const parsedParts = Array.isArray(item.parts) ? normalizeParts(item.parts) : [];
          newItems.push({
            id: item.id || `temp_${Date.now()}_combo`,
            s_no: item.s_no,
            item_type: "COMBO_DISTRIBUTION",
            roof_type: normalizedRoofType,
            structure_height_front: item.structure_height_front || null,
            structure_height_back: item.structure_height_back || null,
            parts: parsedParts,
            tin_shed_structure_type: item.tin_shed_structure_type || null,
            tin_shed_mounting_type: item.tin_shed_mounting_type || null,
            combo_panels_rcc: item.combo_panels_rcc || null,
            combo_panels_ground: item.combo_panels_ground || null,
            combo_panels_tinshed: item.combo_panels_tinshed || null,
            special_structure_type: null,
            special_structure_specs: "",
            special_structure_length: null,
            cleaning_system: "",
            others_text: "",
            status: item.status || "Active"
          });
        });
        
        // Normalize all parts in grouped items
        // Updated by Farhan Khan on 31-Jan-2026
        Object.values(groupedItems).forEach(groupedItem => {
          groupedItem.parts = normalizeParts(groupedItem.parts);
        });
        
        // Assign s_no for items with NULL (data safety)
        newItems.forEach((it, idx) => { if (it.s_no == null) it.s_no = idx + 1; });

        // Fixed By System on 06-Feb-2026 - Only update if structure changed and mark as processed
        if (newItems.length > 0 && newItems.length !== itemsWithParsedParts.length) {
          // Fixed By System on 06-Feb-2026 - Only update if items actually changed to prevent duplication
          if (JSON.stringify(newItems) !== JSON.stringify(items)) {
            onItemsChange(newItems);
            setItemsProcessed(true);
            setInitialItemsLength(newItems.length);
            processedItemsRef.current = itemsKey;
          } else {
            setItemsProcessed(true);
            setInitialItemsLength(newItems.length);
            processedItemsRef.current = itemsKey;
          }
        } else if (itemsWithParsedParts !== items) {
          // Update if parts were parsed from JSONB - assign s_no for NULL (data safety)
          const withSno = itemsWithParsedParts.map((it, idx) => ({ ...it, s_no: it.s_no ?? idx + 1 }));
          // Fixed By System on 06-Feb-2026 - Only update if items actually changed to prevent duplication
          if (JSON.stringify(withSno) !== JSON.stringify(items)) {
            onItemsChange(withSno);
            setItemsProcessed(true);
            setInitialItemsLength(withSno.length);
            processedItemsRef.current = itemsKey;
          } else {
            setItemsProcessed(true);
            setInitialItemsLength(withSno.length);
            processedItemsRef.current = itemsKey;
          }
        } else {
          // Items are already in correct format - assign s_no for NULL (data safety), mark as processed
          const withSno = items.map((it, idx) => ({ ...it, s_no: it.s_no ?? idx + 1 }));
          if (JSON.stringify(withSno) !== JSON.stringify(items)) onItemsChange(withSno);
          setItemsProcessed(true);
          setInitialItemsLength(items.length);
        }
      } else {
        // Items already in correct format, mark as processed
        setItemsProcessed(true);
        setInitialItemsLength(items.length);
      }
    } else {
      // No items, mark as processed to prevent re-running
      setItemsProcessed(true);
      setInitialItemsLength(0);
    }
  }, [items]); // Fixed By System on 06-Feb-2026 - Only depend on items, use ref to prevent re-processing
  
  // Helper to parse JSON array
  const parseJsonArray = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  };
  // Roof Type Options - Value is normalized for API, Label is user-friendly
  const roofTypeOptions = [
    { value: "RCC", label: "RCC" },
    { value: "GROUND", label: "GROUND" },
    { value: "TINSHED", label: "TIN SHED" }, // API value: TINSHED, UI label: TIN SHED
    { value: "COMBO", label: "COMBO" }
  ];

  // Normalize roof type value - ensures consistent format for validation and API
  const normalizeRoofType = (roofType) => {
    if (!roofType) return null;
    
    // Handle React Select object
    const rawValue = roofType?.value || roofType;
    
    // Guard against null/undefined/empty objects
    if (!rawValue || (typeof rawValue === 'object' && Object.keys(rawValue).length === 0)) {
      return null;
    }
    
    // Convert to string, trim, uppercase, remove spaces
    // This automatically converts "TIN SHED" to "TINSHED"
    const normalized = rawValue?.toString().trim().toUpperCase().replace(/\s+/g, '');
    
    // Return normalized value if valid
    const validTypes = ['RCC', 'GROUND', 'TINSHED', 'COMBO'];
    return validTypes.includes(normalized) ? normalized : null;
  };

  // Part Name Options
  const partNameOptions = [
    { value: "LEGS", label: "LEGS" },
    { value: "RAFTER", label: "RAFTER" },
    { value: "PURLIN", label: "PURLIN" },
    { value: "BRACING", label: "BRACING" },
    { value: "FOUNDATION", label: "FOUNDATION" },
    { value: "OTHERS", label: "OTHERS" }
  ];

  // Specs Options
  const specsOptions = [
    { value: "MS", label: "MS" },
    { value: "PRE GI", label: "PRE GI" },
    { value: "HOT DIP", label: "HOT DIP" }
  ];

  // Shape Options
  const shapeOptions = [
    { value: "SQUARE PIPE", label: "SQUARE PIPE" },
    { value: "CHANNEL", label: "CHANNEL" }
  ];

  // Make Options
  const makeOptions = [
    { value: "APPOLO", label: "APPOLO" },
    { value: "TATA", label: "TATA" }
  ];

  // Tin Shed Structure Type Options
  const tinShedStructureTypeOptions = [
    { value: "ALUMINIUM MONO RAIL", label: "ALUMINIUM MONO RAIL" },
    { value: "GI CHANNEL", label: "GI CHANNEL" },
    { value: "RAISED", label: "RAISED" },
    { value: "OTHERS", label: "OTHERS" }
  ];

  // Tin Shed Mounting Type Options
  const tinShedMountingTypeOptions = [
    { value: "SDS", label: "SDS" },
    { value: "RIVIT", label: "RIVIT" },
    { value: "ADHESIVE CHEMICAL", label: "ADHESIVE CHEMICAL" },
    { value: "OTHERS", label: "OTHERS" }
  ];

  // Special Structure Type Options
  const specialStructureTypeOptions = [
    { value: "STAIRS", label: "STAIRS" },
    { value: "RAILING", label: "RAILING" },
    { value: "WALKWAY", label: "WALKWAY" },
    { value: "LIFE SUPPORT", label: "LIFE SUPPORT" },
    { value: "CLEANING SYSTEM", label: "CLEANING SYSTEM" },
    { value: "OTHERS", label: "OTHERS" }
  ];

  // Handle BOM header changes
  const handleBomChange = (e) => {
    const { name, value } = e.target;
    onBomChange({ ...bom, [name]: value });
  };

  // Handle line item changes
  const handleLineItemChange = (lineItemIndex, field, value) => {
    const newItems = [...items];
    if (!newItems[lineItemIndex]) {
      newItems[lineItemIndex] = {
        id: `temp_${Date.now()}_${lineItemIndex}`,
        item_type: "COMPONENT",
        roof_type: "",
        structure_height_front: null,
        structure_height_back: null,
        parts: [],
        tin_shed_structure_type: null,
        tin_shed_mounting_type: null,
        combo_panels_rcc: null,
        combo_panels_ground: null,
        combo_panels_tinshed: null,
        special_structure_type: null,
        special_structure_specs: "",
        special_structure_length: null,
        cleaning_system: "",
        others_text: "",
        status: "Active"
      };
    }
    
    // Normalize roof_type value before storing
    let normalizedValue = value;
    if (field === 'roof_type') {
      normalizedValue = normalizeRoofType(value);
      // Ensure we don't store null/undefined - use empty string if invalid
      if (!normalizedValue && value !== null && value !== undefined) {
        normalizedValue = ""; // Invalid value becomes empty string
      }
    }
    // tin_shed_structure_type: convert multi-select array to comma-separated string for DB
    if (field === 'tin_shed_structure_type') {
      if (Array.isArray(value)) {
        normalizedValue = value.map(item => item?.value ?? item).filter(Boolean).join(',') || null;
      } else if (value === null || value === undefined || value === '') {
        normalizedValue = null;
      }
    }

    newItems[lineItemIndex] = { ...newItems[lineItemIndex], [field]: normalizedValue };
    onItemsChange(newItems);
  };

  // Handle part changes within a line item
  // Updated by Farhan Khan on 31-Jan-2026 - Normalize parts structure
  // Fixed by Farhan Khan on 31-Jan-2026 - Don't filter out parts without component_name during editing
  // Fixed by Farhan Khan on 31-Jan-2026 - Handle multi-select arrays for shape and make
  const handlePartChange = (lineItemIndex, partIndex, field, value) => {
    const newItems = [...items];
    
    // Ensure parts array exists
    if (!newItems[lineItemIndex].parts || !Array.isArray(newItems[lineItemIndex].parts)) {
      newItems[lineItemIndex].parts = [];
    }
    
    // Ensure part object exists at index
    // Fixed by Farhan Khan on 31-Jan-2026 - Initialize shape and make as arrays
    if (!newItems[lineItemIndex].parts[partIndex]) {
      newItems[lineItemIndex].parts[partIndex] = {
        component_name: "",
        specs: "",
        size: "",
        shape: [], // Multi-select array
        make: [],  // Multi-select array
        others: ""
      };
    }
    
    // Update field value
    // Map part_name to component_name for consistency
    const mappedField = field === 'part_name' ? 'component_name' : field;
    
    // Fixed by Farhan Khan on 31-Jan-2026 - Handle multi-select arrays for shape and make
    // React Select multi-select returns array of objects [{value, label}, ...] or null
    let processedValue = value;
    if (mappedField === 'shape' || mappedField === 'make') {
      // Handle multi-select: convert array of objects to array of values
      if (Array.isArray(value)) {
        processedValue = value.map(item => item.value || item);
      } else if (value === null || value === undefined) {
        processedValue = [];
      } else if (typeof value === 'string') {
        // Handle legacy string format - convert to array
        processedValue = value ? [value] : [];
      } else {
        processedValue = [];
      }
    }
    
    newItems[lineItemIndex].parts[partIndex] = {
      ...newItems[lineItemIndex].parts[partIndex],
      [mappedField]: processedValue
    };
    
    // Don't normalize/filter parts during editing - allow empty component_name
    // Only trim values but keep all parts (normalization happens before save in BomEdit.jsx)
    // Trim component_name if it exists
    if (newItems[lineItemIndex].parts[partIndex].component_name) {
      newItems[lineItemIndex].parts[partIndex].component_name = 
        newItems[lineItemIndex].parts[partIndex].component_name.trim();
    }
    
    onItemsChange(newItems);
  };
  
  // Normalize parts array - ensures clean structure for JSONB storage
  // Updated by Farhan Khan on 31-Jan-2026
  // Fixed by Farhan Khan on 31-Jan-2026 - Trim component_name values
  // Fixed by Farhan Khan on 31-Jan-2026 - Handle multi-select arrays for shape and make
  const normalizeParts = (parts) => {
    // Ensure parts is always an array
    if (!parts) return [];
    if (!Array.isArray(parts)) return [];
    
    // Filter out null/undefined entries and normalize each part
    return parts
      .filter(part => part !== null && part !== undefined)
      .map(part => {
        // Normalize shape - ensure it's an array
        let normalizedShape = [];
        if (Array.isArray(part.shape)) {
          normalizedShape = part.shape.filter(s => s); // Remove empty values
        } else if (part.shape && typeof part.shape === 'string') {
          // Handle legacy string format
          normalizedShape = part.shape.split(',').map(s => s.trim()).filter(s => s);
        }
        
        // Normalize make - ensure it's an array
        let normalizedMake = [];
        if (Array.isArray(part.make)) {
          normalizedMake = part.make.filter(m => m); // Remove empty values
        } else if (part.make && typeof part.make === 'string') {
          // Handle legacy string format
          normalizedMake = part.make.split(',').map(m => m.trim()).filter(m => m);
        }
        
        return {
          component_name: (part.component_name || part.part_name || "").trim(), // Trim component_name
          specs: part.specs || "",
          size: part.size || "",
          shape: normalizedShape, // Array for multi-select
          make: normalizedMake,   // Array for multi-select
          others: part.others || ""
        };
      })
      .filter(part => part.component_name); // Remove parts without component_name after trimming
  };

  // Add new line item - s_no auto-increments
  const addLineItem = () => {
    const newLineItem = {
      id: `temp_${Date.now()}`,
      s_no: items.length + 1,
      item_type: "COMPONENT",
      roof_type: "",
      structure_height_front: null,
      structure_height_back: null,
      parts: [],
      tin_shed_structure_type: null,
      tin_shed_mounting_type: null,
      combo_panels_rcc: null,
      combo_panels_ground: null,
      combo_panels_tinshed: null,
      special_structure_type: null,
      special_structure_specs: "",
      special_structure_length: null,
      cleaning_system: "",
      others_text: "",
      status: "Active"
    };
    onItemsChange([...items, newLineItem]);
  };

  // Remove line item - re-sequence s_no for remaining rows
  const removeLineItem = (index) => {
    const newItems = items.filter((_, i) => i !== index);
    newItems.forEach((item, i) => { item.s_no = i + 1; });
    onItemsChange(newItems);
  };

  // Add part to line item
  // Updated by Farhan Khan on 31-Jan-2026 - Use normalized part structure
  // Fixed by Farhan Khan on 31-Jan-2026 - Don't normalize immediately to allow empty component_name during editing
  // Fixed by Farhan Khan on 31-Jan-2026 - Initialize shape and make as arrays for multi-select
  const addPart = (lineItemIndex) => {
    const newItems = [...items];
    if (!newItems[lineItemIndex].parts || !Array.isArray(newItems[lineItemIndex].parts)) {
      newItems[lineItemIndex].parts = [];
    }
    // Initialize new part with all required fields
    // Fixed by Farhan Khan on 31-Jan-2026 - shape and make must be arrays for multi-select
    newItems[lineItemIndex].parts.push({
      component_name: "",
      specs: "",
      size: "",
      shape: [], // Multi-select array
      make: [],  // Multi-select array
      others: ""
    });
    // Don't normalize parts immediately - allow empty component_name during editing
    // Normalization will happen before save in BomEdit.jsx
    onItemsChange(newItems);
  };

  // Remove part from line item
  const removePart = (lineItemIndex, partIndex) => {
    const newItems = [...items];
    if (newItems[lineItemIndex].parts) {
      newItems[lineItemIndex].parts = newItems[lineItemIndex].parts.filter(
        (_, i) => i !== partIndex
      );
      onItemsChange(newItems);
    }
  };

  // Custom ValueContainer to prevent multi-select from expanding row height
  const StructureTypeValueContainer = ({ children, ...props }) => {
    const values = props.getValue();
    const count = values?.length ?? 0;
    return (
      <components.ValueContainer {...props}>
        {count > 2 ? `${count} selected` : children}
      </components.ValueContainer>
    );
  };

  const structureTypeSelectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    control: (base) => ({ ...base, minHeight: "36px", height: "36px" }),
    valueContainer: (base) => ({ ...base, height: "36px", overflow: "hidden" }),
  };

  // Get placeholder for size based on part name
  // Updated by Farhan Khan on 31-Jan-2026 - Handle both component_name and part_name
  const getSizePlaceholder = (part) => {
    const partName = part?.component_name || part?.part_name || "";
    if (partName === "FOUNDATION") {
      return "300 MM X 300 MM X 300 MM";
    }
    return "75 MM X 75 MM X 2 MM";
  };

  return (
    <>
      {/* BOM Header Section - BOM Name removed from UI, kept internally for save */}
      <Row className="ibs-edit-form pb-3">
        <Row lg={12}>
          <Col lg={6}>
            <Form.Group className="mx-3">
              <Form.Label className="form-view-label" htmlFor="formBasicStatus">
                Status
              </Form.Label>
              <Form.Select
                aria-label="Select status"
                name="status"
                value={bom.status || ""}
                onChange={handleBomChange}
                required
              >
                <option value="">--Select--</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>
      </Row>

      {/* Line Items Section */}
      <Row className="mt-4">
        <Col lg={12}>
          <Row className="view-form-header">
            <Col lg={8}>
              <h5>Mounting Structure Line Items</h5>
            </Col>
            <Col lg={4} className="d-flex justify-content-end">
              <BootstrapButton
                size="sm"
                // className="btn-create"
                variant="success"
                onClick={addLineItem}
              >
                Add Line Item
              </BootstrapButton>
            </Col>
          </Row>

          {items.length === 0 ? (
            <div className="text-center my-4 p-4 border rounded">
              <p className="text-muted mb-0">
                No line items added. Click "Add Line Item" to add items.
              </p>
            </div>
          ) : (
            items.map((lineItem, lineItemIndex) => (
              <Card key={lineItem.id || lineItemIndex} className="mb-4 shadow-sm">
                <Card.Header className="bg-light d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">S.No {lineItem.s_no ?? lineItemIndex + 1} | Line Item #{lineItemIndex + 1}</h6>
                  <BootstrapButton
                    size="sm"
                    variant="danger"
                    onClick={() => removeLineItem(lineItemIndex)}
                  >
                    <i className="fa fa-trash"></i> Remove Line Item
                  </BootstrapButton>
                </Card.Header>
                <Card.Body>
                  {/* ROOF TYPE */}
                  <Row className="mb-3">
                    <Col lg={6}>
                      <Form.Group>
                        <Form.Label className="form-view-label">
                          ROOF TYPE 
                        </Form.Label>
                        <Select
                          placeholder="Select Roof Type..."
                          onChange={(selected) => {
                            // Normalize value before passing to handler
                            const normalizedValue = normalizeRoofType(selected);
                            handleLineItemChange(
                              lineItemIndex,
                              "roof_type",
                              normalizedValue || ""
                            );
                          }}
                          options={roofTypeOptions}
                          value={roofTypeOptions.find(
                            (opt) => {
                              // Normalize both values for comparison
                              const normalizedOptValue = normalizeRoofType(opt.value);
                              const normalizedItemValue = normalizeRoofType(lineItem.roof_type);
                              return normalizedOptValue === normalizedItemValue;
                            }
                          )}
                          isClearable={false}
                          required
                          styles={{
                            control: (base) => ({
                              ...base,
                              minHeight: "38px",
                              height: "38px"
                            })
                          }}
                        />
                      </Form.Group>
                    </Col>
                    <Col lg={6}>
                      <Form.Group>
                        <Form.Label className="form-view-label">
                          Status
                        </Form.Label>
                        <Form.Select
                          value={lineItem.status || "Active"}
                          onChange={(e) =>
                            handleLineItemChange(
                              lineItemIndex,
                              "status",
                              e.target.value
                            )
                          }
                        >
                          <option value="Active">Active</option>
                          <option value="Inactive">Inactive</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* STRUCTURE HEIGHT */}
                  <Row className="mb-3">
                    <Col lg={6}>
                      <Form.Group>
                        <Form.Label className="form-view-label">
                          STRUCTURE HEIGHT - FRONT (FT) 
                        </Form.Label>
                        <Form.Control
                          type="number"
                          step="0.01"
                          placeholder="Enter front height"
                          value={lineItem.structure_height_front || ""}
                          onChange={(e) =>
                            handleLineItemChange(
                              lineItemIndex,
                              "structure_height_front",
                              e.target.value ? parseFloat(e.target.value) : null
                            )
                          }
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col lg={6}>
                      <Form.Group>
                        <Form.Label className="form-view-label">
                          STRUCTURE HEIGHT - BACK (FT) 
                        </Form.Label>
                        <Form.Control
                          type="number"
                          step="0.01"
                          placeholder="Enter back height"
                          value={lineItem.structure_height_back || ""}
                          onChange={(e) =>
                            handleLineItemChange(
                              lineItemIndex,
                              "structure_height_back",
                              e.target.value ? parseFloat(e.target.value) : null
                            )
                          }
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* PART DETAILS */}
                  <Row className="mb-3">
                    <Col lg={12}>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="mb-0">PART DETAILS</h6>
                        <BootstrapButton
                          size="sm"
                          variant="outline-primary"
                          onClick={() => addPart(lineItemIndex)}
                        >
                          <i className="fa fa-plus"></i> Add Part
                        </BootstrapButton>
                      </div>
                      {(!lineItem.parts || !Array.isArray(lineItem.parts) || lineItem.parts.length === 0) ? (
                        <div className="text-center p-3 border rounded bg-light">
                          <p className="text-muted mb-0">
                            No parts added. Click "Add Part" to add parts.
                          </p>
                        </div>
                      ) : (
                        <Table striped bordered hover size="sm">
                          <thead className="bg-custom text-white">
                            <tr>
                              <th>PART NAME</th>
                              <th>SPECS</th>
                              <th>SIZE</th>
                              <th>SHAPE</th>
                              <th>MAKE</th>
                              {/* <th>OTHERS</th> */}
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(Array.isArray(lineItem.parts) ? lineItem.parts : []).map((part, partIndex) => (
                              <tr key={`part-${lineItemIndex}-${partIndex}`}>
                                <td>
                                  <Select
                                    placeholder="Select..."
                                    onChange={(selected) =>
                                      handlePartChange(
                                        lineItemIndex,
                                        partIndex,
                                        "part_name",
                                        selected ? selected.value : ""
                                      )
                                    }
                                    options={partNameOptions}
                                    value={partNameOptions.find(
                                      (opt) => opt.value === (part.component_name || part.part_name)
                                    )}
                                    isClearable
                                    styles={{
                                      control: (base) => ({
                                        ...base,
                                        minHeight: "31px",
                                        height: "31px"
                                      })
                                    }}
                                  />
                                </td>
                                <td>
                                  <Select
                                    placeholder="Select..."
                                    onChange={(selected) =>
                                      handlePartChange(
                                        lineItemIndex,
                                        partIndex,
                                        "specs",
                                        selected ? selected.value : null
                                      )
                                    }
                                    options={specsOptions}
                                    value={specsOptions.find(
                                      (opt) => opt.value === part.specs
                                    )}
                                    isClearable
                                    styles={{
                                      control: (base) => ({
                                        ...base,
                                        minHeight: "31px",
                                        height: "31px"
                                      })
                                    }}
                                  />
                                </td>
                                <td>
                                  <Form.Control
                                    size="sm"
                                    type="text"
                                    placeholder={getSizePlaceholder(part)}
                                    value={part.size || ""}
                                    onChange={(e) =>
                                      handlePartChange(
                                        lineItemIndex,
                                        partIndex,
                                        "size",
                                        e.target.value
                                      )
                                    }
                                  />
                                </td>
                                <td>
                                  <Select
                                    placeholder="Select..."
                                    isMulti
                                    onChange={(selected) =>
                                      handlePartChange(
                                        lineItemIndex,
                                        partIndex,
                                        "shape",
                                        selected || []
                                      )
                                    }
                                    options={shapeOptions}
                                    value={shapeOptions.filter(
                                      (opt) => Array.isArray(part.shape) 
                                        ? part.shape.includes(opt.value)
                                        : (part.shape === opt.value || (typeof part.shape === 'string' && part.shape && part.shape.split(',').includes(opt.value)))
                                    )}
                                    isClearable
                                    styles={{
                                      control: (base) => ({
                                        ...base,
                                        minHeight: "31px",
                                        height: "31px"
                                      })
                                    }}
                                  />
                                </td>
                                <td>
                                  <Select
                                    placeholder="Select..."
                                    isMulti
                                    onChange={(selected) =>
                                      handlePartChange(
                                        lineItemIndex,
                                        partIndex,
                                        "make",
                                        selected || []
                                      )
                                    }
                                    options={makeOptions}
                                    value={makeOptions.filter(
                                      (opt) => Array.isArray(part.make)
                                        ? part.make.includes(opt.value)
                                        : (part.make === opt.value || (typeof part.make === 'string' && part.make && part.make.split(',').includes(opt.value)))
                                    )}
                                    isClearable
                                    styles={{
                                      control: (base) => ({
                                        ...base,
                                        minHeight: "31px",
                                        height: "31px"
                                      })
                                    }}
                                  />
                                </td>
                                {/* <td>
                                  {(part.component_name || part.part_name) === "OTHERS" ? (
                                    <Form.Control
                                      size="sm"
                                      type="text"
                                      placeholder="Enter others details"
                                      value={part.others || ""}
                                      onChange={(e) =>
                                        handlePartChange(
                                          lineItemIndex,
                                          partIndex,
                                          "others",
                                          e.target.value
                                        )
                                      }
                                    />
                                  ) : (
                                    <span className="text-muted">-</span>
                                  )}
                                </td> */}
                                <td>
                                  <BootstrapButton
                                    size="sm"
                                    variant="danger"
                                    onClick={() => removePart(lineItemIndex, partIndex)}
                                  >
                                    <i className="fa fa-trash"></i>
                                  </BootstrapButton>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      )}
                    </Col>
                  </Row>

                  {/* TIN SHED STRUCTURE - Conditional */}
                  {normalizeRoofType(lineItem.roof_type) === "TINSHED" && (
                    <Row className="mb-3">
                      <Col lg={12}>
                        <h6 className="mb-3">TIN SHED STRUCTURE</h6>
                      </Col>
                      <Col lg={6}>
                        <Form.Group>
                          <Form.Label className="form-view-label">
                            STRUCTURE TYPE
                          </Form.Label>
                          <Select
                            name="structure_type"
                            placeholder="Select Structure Type..."
                            isMulti
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            components={{ ValueContainer: StructureTypeValueContainer }}
                            onChange={(selected) =>
                              handleLineItemChange(
                                lineItemIndex,
                                "tin_shed_structure_type",
                                selected || []
                              )
                            }
                            options={tinShedStructureTypeOptions}
                            value={tinShedStructureTypeOptions.filter((opt) =>
                              (lineItem.tin_shed_structure_type || "")
                                .split(",")
                                .map((s) => s.trim())
                                .filter(Boolean)
                                .includes(opt.value)
                            )}
                            isClearable
                            styles={structureTypeSelectStyles}
                          />
                        </Form.Group>
                      </Col>
                      <Col lg={6}>
                        <Form.Group>
                          <Form.Label className="form-view-label">
                            MOUNTING TYPE
                          </Form.Label>
                          <Select
                            placeholder="Select Mounting Type..."
                            onChange={(selected) =>
                              handleLineItemChange(
                                lineItemIndex,
                                "tin_shed_mounting_type",
                                selected ? selected.value : null
                              )
                            }
                            options={tinShedMountingTypeOptions}
                            value={tinShedMountingTypeOptions.find(
                              (opt) => opt.value === lineItem.tin_shed_mounting_type
                            )}
                            isClearable
                            styles={{
                              control: (base) => ({
                                ...base,
                                minHeight: "38px",
                                height: "38px"
                              })
                            }}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  )}

                  {/* COMBO STRUCTURE - Conditional */}
                  {normalizeRoofType(lineItem.roof_type) === "COMBO" && (
                    <Row className="mb-3">
                      <Col lg={12}>
                        <h6 className="mb-3">COMBO STRUCTURE</h6>
                      </Col>
                      <Col lg={4}>
                        <Form.Group>
                          <Form.Label className="form-view-label">
                            NUMBER OF PANELS - RCC
                          </Form.Label>
                          <Form.Control
                            type="number"
                            placeholder="Enter RCC panels"
                            value={lineItem.combo_panels_rcc || ""}
                            onChange={(e) =>
                              handleLineItemChange(
                                lineItemIndex,
                                "combo_panels_rcc",
                                e.target.value ? parseInt(e.target.value) : null
                              )
                            }
                          />
                        </Form.Group>
                      </Col>
                      <Col lg={4}>
                        <Form.Group>
                          <Form.Label className="form-view-label">
                            NUMBER OF PANELS - GROUND
                          </Form.Label>
                          <Form.Control
                            type="number"
                            placeholder="Enter Ground panels"
                            value={lineItem.combo_panels_ground || ""}
                            onChange={(e) =>
                              handleLineItemChange(
                                lineItemIndex,
                                "combo_panels_ground",
                                e.target.value ? parseInt(e.target.value) : null
                              )
                            }
                          />
                        </Form.Group>
                      </Col>
                      <Col lg={4}>
                        <Form.Group>
                          <Form.Label className="form-view-label">
                            NUMBER OF PANELS - TINSHED
                          </Form.Label>
                          <Form.Control
                            type="number"
                            placeholder="Enter Tin Shed panels"
                            value={lineItem.combo_panels_tinshed || ""}
                            onChange={(e) =>
                              handleLineItemChange(
                                lineItemIndex,
                                "combo_panels_tinshed",
                                e.target.value ? parseInt(e.target.value) : null
                              )
                            }
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  )}

                  {/* SPECIAL STRUCTURE */}
                  <Row className="mb-3">
                    <Col lg={12}>
                      <h6 className="mb-3">SPECIAL STRUCTURE</h6>
                    </Col>
                    <Col lg={6}>
                      <Form.Group>
                        <Form.Label className="form-view-label">
                          TYPE
                        </Form.Label>
                        <Select
                          placeholder="Select Special Structure Type..."
                          onChange={(selected) =>
                            handleLineItemChange(
                              lineItemIndex,
                              "special_structure_type",
                              selected ? selected.value : null
                            )
                          }
                          options={specialStructureTypeOptions}
                          value={specialStructureTypeOptions.find(
                            (opt) => opt.value === lineItem.special_structure_type
                          )}
                          isClearable
                          styles={{
                            control: (base) => ({
                              ...base,
                              minHeight: "38px",
                              height: "38px"
                            })
                          }}
                        />
                      </Form.Group>
                    </Col>
                    <Col lg={6}>
                      <Form.Group>
                        <Form.Label className="form-view-label">
                          LENGTH
                        </Form.Label>
                        <Form.Control
                          type="number"
                          step="0.01"
                          placeholder="Enter length"
                          value={lineItem.special_structure_length || ""}
                          onChange={(e) =>
                            handleLineItemChange(
                              lineItemIndex,
                              "special_structure_length",
                              e.target.value ? parseFloat(e.target.value) : null
                            )
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col lg={12}>
                      <Form.Group>
                        <Form.Label className="form-view-label">
                          SPECS
                        </Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          placeholder="Enter specifications"
                          value={lineItem.special_structure_specs || ""}
                          onChange={(e) =>
                            handleLineItemChange(
                              lineItemIndex,
                              "special_structure_specs",
                              e.target.value
                            )
                          }
                        />
                      </Form.Group>
                    </Col>
                    {lineItem.special_structure_type === "CLEANING SYSTEM" && (
                      <Col lg={12}>
                        <Form.Group>
                          <Form.Label className="form-view-label">
                            CLEANING SYSTEM
                          </Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            placeholder="Enter cleaning system details"
                            value={lineItem.cleaning_system || ""}
                            onChange={(e) =>
                              handleLineItemChange(
                                lineItemIndex,
                                "cleaning_system",
                                e.target.value
                              )
                            }
                          />
                        </Form.Group>
                      </Col>
                    )}
                    {lineItem.special_structure_type === "OTHERS" && (
                      <Col lg={12}>
                        <Form.Group>
                          <Form.Label className="form-view-label">
                            OTHERS
                          </Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            placeholder="Enter other details"
                            value={lineItem.others_text || ""}
                            onChange={(e) =>
                              handleLineItemChange(
                                lineItemIndex,
                                "others_text",
                                e.target.value
                              )
                            }
                          />
                        </Form.Group>
                      </Col>
                    )}
                  </Row>
                </Card.Body>
              </Card>
            ))
          )}
        </Col>
      </Row>
    </>
  );
};

export default MountingStructureForm;
